import { Command as CommandPrimitive } from 'cmdk'
import React, { useState } from 'react'
import { Command, CommandEmpty, CommandGroup, CommandItem, CommandList } from './ui/command'
import { Input } from './ui/input'
import { Popover, PopoverAnchor, PopoverContent } from './ui/popover'
import { Skeleton } from './ui/skeleton'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'

export type GitHubUser = {
  value: string
  label: string
  avatarUrl: string
}

type Props = {
  onSelectedValueChange: (value: GitHubUser | null) => void
  searchValue: string
  onSearchValueChange: (value: string) => void
  items: GitHubUser[]
  isLoading: boolean
  emptyMessage?: string
  placeholder?: string
  disabled?: boolean
  error: Error | null
}

export function Autocomplete({
  onSelectedValueChange,
  searchValue,
  onSearchValueChange,
  items,
  isLoading,
  emptyMessage = 'No Github users found. Search by username, not email.',
  placeholder = 'Search...',
  disabled,
  error,
}: Props) {
  const [open, setOpen] = useState(false)

  const reset = () => {
    onSearchValueChange('')
    onSelectedValueChange(null)
  }

  const onInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    if (!e.relatedTarget?.hasAttribute('cmdk-list')) {
      reset()
    }
  }

  const onSelectItem = (inputValue: GitHubUser) => {
    onSearchValueChange(inputValue.value)
    onSelectedValueChange(inputValue)
    setOpen(false)
  }

  const queryEnabled = searchValue.length >= 1
  return (
    <div className="flex items-center">
      <Popover open={open} onOpenChange={setOpen}>
        <Command shouldFilter={false}>
          <PopoverAnchor asChild>
            <CommandPrimitive.Input
              asChild
              value={searchValue}
              onValueChange={onSearchValueChange}
              onKeyDown={(e) => setOpen(e.key !== 'Escape')}
              onMouseDown={() => setOpen((open) => !!searchValue || !open)}
              onFocus={() => setOpen(true)}
              onBlur={onInputBlur}
              disabled={disabled}
            >
              <Input placeholder={placeholder} disabled={disabled} autoCapitalize="none" />
            </CommandPrimitive.Input>
          </PopoverAnchor>
          {!open && <CommandList aria-hidden="true" className="hidden" />}
          {queryEnabled && (
            <PopoverContent
              asChild
              onOpenAutoFocus={(e) => e.preventDefault()}
              onInteractOutside={(e) => {
                if (e.target instanceof Element && e.target.hasAttribute('cmdk-input')) {
                  e.preventDefault()
                }
              }}
              className="w-(--radix-popover-trigger-width) p-0"
            >
              <CommandList>
                {isLoading && (
                  <CommandPrimitive.Loading>
                    <div className="p-1">
                      <Skeleton className="h-6 w-full" />
                    </div>
                  </CommandPrimitive.Loading>
                )}
                {items.length > 0 && !isLoading ? (
                  <CommandGroup>
                    {items.map((option) => (
                      <CommandItem
                        key={option.value}
                        value={option.value}
                        onMouseDown={(e) => e.preventDefault()}
                        onSelect={() => onSelectItem(option)}
                      >
                        <Avatar className="h-6 w-6 mr-2">
                          <AvatarImage src={option.avatarUrl} />
                          <AvatarFallback>{option.label[0].toUpperCase()}</AvatarFallback>
                        </Avatar>
                        {option.label}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                ) : null}
                <ErrorOrEmpty
                  error={error}
                  searchValue={searchValue}
                  emptyMessage={emptyMessage}
                  isLoading={isLoading}
                />
              </CommandList>
            </PopoverContent>
          )}
        </Command>
      </Popover>
    </div>
  )
}
const ErrorOrEmpty = ({
  error,
  searchValue,
  isLoading,
  emptyMessage,
}: {
  error?: Error | null
  searchValue: string
  isLoading: boolean
  emptyMessage: string
}) => {
  if (error) {
    return (
      <CommandEmpty>
        <div className="p-1">
          <div className="text-sm text-error">{error.message}</div>
        </div>
      </CommandEmpty>
    )
  }
  if (!isLoading && !!searchValue) {
    return <CommandEmpty>{emptyMessage}</CommandEmpty>
  }
  return null
}
